import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, GitBranch, Send, AlertTriangle, CheckCircle2, Lock, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Folio, StatusBadge, Empty } from "../components/common";
import CommercialTab from "../components/quote/CommercialTab";
import LinesTab from "../components/quote/LinesTab";
import FiscalTab from "../components/quote/FiscalTab";
import TechTab from "../components/quote/TechTab";
import RevisionsTab from "../components/quote/RevisionsTab";
import PdfTab from "../components/quote/PdfTab";
import AcceptanceTab from "../components/quote/AcceptanceTab";
import { api, errMsg, downloadPdf } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { money, pct } from "../lib/format";

const EDITABLE = ["title", "customer_id", "plant_id", "ship_to_plant_id", "contact_id", "machine_ids", "sales_person_id", "currency", "exchange_rate",
  "quote_date", "valid_until", "terms", "remarks", "lines", "technicians", "tax_rule_ids", "price_list_id"];

function useMasters(q) {
  const [m, setM] = useState({});
  const key = q && q.customer_id ? `${q.customer_id}|${q.country}` : null;
  useEffect(() => {
    if (!key) { api.get("/customers").then((r) => setM({ customers: r.data })).catch(() => {}); return; }
    const [cid, country] = key.split("|");
    const get = (u) => api.get(u).then((r) => r.data).catch(() => []);
    Promise.all([get(`/customers/${cid}`), get(`/machines?customer_id=${cid}`), get("/services"), get(`/price-lists?country=${country}`),
      get(`/technicians?country=${country}`), get(`/tax-rules?country=${country}`), get("/users/lite"), get("/customers")])
      .then(([customer, machines, services, priceLists, technicians, taxRules, users, customers]) =>
        setM({ customer, machines, services, priceLists, technicians, taxRules, users, customers }));
  }, [key]);
  return m;
}

const Summary = ({ calc, currency, showCost }) => {
  const { t } = useT();
  const tt = calc.totals;
  const cells = [["ws.net", money(tt.net, currency), "sum-net"], showCost && ["ws.cost", money(tt.cost, currency), "sum-cost"],
    showCost && ["ws.gp", money(tt.gross_profit, currency), "sum-gp"], showCost && ["q.margin", pct(tt.margin_pct), "sum-margin"],
    ["ws.taxes", money(tt.tax_total, currency), "sum-tax"], ["ws.total", money(tt.total, currency), "sum-total"], ["ws.usd", money(tt.total_usd, "USD"), "sum-usd"]].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-px overflow-hidden rounded-md border border-neutral-200 bg-neutral-200">
      {cells.map(([k, v, id]) => (
        <div key={k} className="min-w-[150px] flex-1 bg-white px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{t(k)}</div>
          <div data-testid={id} className={`mt-1 font-mono text-sm font-bold ${k === "ws.total" ? "text-[#db0720]" : ""}`}>{v}</div>
        </div>
      ))}
    </div>
  );
};

const Warnings = ({ calc }) => {
  const { t } = useT();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="calc-warnings">
      {calc.totals.tax_definitive
        ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" />{t("ws.taxDef")}</span>
        : <span data-testid="tax-non-definitive-badge" className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"><AlertTriangle className="h-3 w-3" />{t("ws.taxNonDef")}</span>}
      {calc.warnings.map((w, i) => (
        <span key={i} className="rounded-sm border border-neutral-200 bg-white px-2 py-0.5 text-[11px] text-neutral-700">
          {t(`w.${w.code}`)}{w.msg ? `: ${w.msg}` : ""}
        </span>
      ))}
    </div>
  );
};

function PublishDialog({ mode, onClose, onDone, qid }) {
  const { t } = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [mode]);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${qid}/publish`, { reason, send: mode === "send" });
      toast.success(`Rev ${r.data.rev}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!mode} onOpenChange={onClose}>
      <DialogContent data-testid="publish-dialog">
        <DialogHeader><DialogTitle>{mode === "send" ? t("ws.publish") : t("ws.createRev")}</DialogTitle>
          <DialogDescription>{t("ws.publishConfirm")}</DialogDescription></DialogHeader>
        <Textarea data-testid="publish-reason-input" placeholder={t("ws.reason")} value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button data-testid="publish-confirm-btn" disabled={busy} onClick={go}>{busy ? "…" : t("common.yes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionNav({ keys, children }) {
  const { t } = useT();
  const [active, setActive] = useState(keys[0]);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.dataset.key);
    }, { rootMargin: "-130px 0px -55% 0px" });
    keys.forEach((k) => { const el = document.getElementById(`sec-${k}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.join(",")]);
  const go = (k) => { setActive(k); document.getElementById(`sec-${k}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <>
      <nav className="sticky top-14 z-20 -mx-1 rounded-md border border-neutral-200 bg-white/95 p-1 shadow-sm backdrop-blur" data-testid="section-nav">
        <select data-testid="section-nav-select" value={active} onChange={(e) => go(e.target.value)}
          className="h-9 w-full rounded border-0 bg-transparent px-2 text-sm font-semibold text-[#db0720] focus:outline-none sm:hidden">
          {keys.map((k, i) => <option key={k} value={k}>{i + 1}. {t(`ws.${k}`)}</option>)}
        </select>
        <div className="hidden flex-wrap gap-1 sm:flex">
        {keys.map((k, i) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => go(k)}
            className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${active === k ? "bg-[#db0720] text-white" : "text-neutral-700 hover:bg-neutral-100"}`}>
            <span className="mr-1.5 opacity-60">{i + 1}.</span>{t(`ws.${k}`)}
          </button>
        ))}
        </div>
      </nav>
      <div className="space-y-10">
        {keys.map((k, i) => (
          <section key={k} id={`sec-${k}`} data-key={k} className="scroll-mt-32" data-testid={`sec-${k}`}>
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#db0720] text-[11px] font-bold text-white">{i + 1}</span>
              <h2 className="text-base font-bold tracking-tight md:text-lg">{t(`ws.${k}`)}</h2>
              <span className="h-px flex-1 bg-neutral-200" />
            </div>
            {children[k]}
          </section>
        ))}
      </div>
    </>
  );
}

export default function QuoteWorkspace() {
  const { id } = useParams();
  const { t } = useT();
  const { can, user } = useAuth();
  const [q, setQ] = useState(null);
  const [d, setD] = useState(null);
  const [changed, setChanged] = useState(false);
  const [mode, setMode] = useState(null);
  const [revKey, setRevKey] = useState(0);
  const isNew = id === "new";
  const nav = useNavigate();
  const m = useMasters(d);
  const load = useCallback(async () => {
    if (isNew) {
      const rate = (await api.get("/exchange-rates/current").catch(() => ({ data: {} }))).data;
      const today = new Date(); const vu = new Date(Date.now() + 30 * 864e5);
      const blank = { id: null, folio: t("q.newFolio"), status: "draft", current_rev: null, customer_id: "", country: "", currency: "MXN", exchange_rate: rate.rate || 0,
        quote_date: today.toISOString().slice(0, 10), valid_until: vu.toISOString().slice(0, 10), terms: "Net 30", remarks: "", title: "", lines: [], technicians: [],
        machine_ids: [], tax_rule_ids: [], sales_person_id: user?.id, resolved: {}, calc: { lines: [], taxes: [], warnings: [], machine_allocation: [], labor_rule: { hours_per_day: "—", overtime_factor: "—", min_margin_pct: "—" },
          totals: { net: 0, cost: 0, gross_profit: 0, margin_pct: 0, tax_total: 0, total: 0, total_usd: 0, tax_definitive: false } } };
      setQ(blank); setD(blank); setChanged(false);
      return;
    }
    const r = await api.get(`/quotations/${id}`);
    setQ(r.data);
    setD(r.data);
    setChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load().catch(() => setQ(false)); }, [load]);
  if (q === false) return (
    <div className="py-16 text-center" data-testid="quote-not-found">
      <p className="mb-4 text-sm text-neutral-600">{t("q.notFound")}</p>
      <Button asChild variant="outline"><Link to="/quotations">{t("q.title")}</Link></Button>
    </div>
  );
  if (!q || !d) return <Empty>{t("common.loading")}</Empty>;
  const closed = ["accepted", "rejected", "cancelled"].includes(q.status);
  const locked = closed || !can("quote:write");
  const set = (patch) => { setD((x) => ({ ...x, ...patch })); setChanged(true); };
  const save = async () => {
    try {
      if (isNew) {
        if (!d.customer_id) { toast.error(`${t("common.required")}: ${t("q.customer")}`); return false; }
        const c = (await api.post("/quotations", { customer_id: d.customer_id, currency: d.currency, title: d.title })).data;
        const keys = EDITABLE.filter((k) => !(k in { customer_id: 1, exchange_rate: 1 }) && JSON.stringify(d[k] ?? null) !== JSON.stringify(c[k] ?? null) && !(k === "remarks" && !d.remarks));
        if (keys.length) await api.put(`/quotations/${c.id}`, Object.fromEntries(keys.map((k) => [k, d[k]])));
        toast.success(`${c.folio} · ${t("common.saved")}`);
        nav(`/quotations/${c.id}`, { replace: true });
        return true;
      }
      const keys = EDITABLE.filter((k) => JSON.stringify(d[k] ?? null) !== JSON.stringify(q[k] ?? null));
      if (!keys.length) { setChanged(false); toast.success(t("common.saved")); return true; }
      const r = await api.put(`/quotations/${id}`, Object.fromEntries(keys.map((k) => [k, d[k]])));
      setQ(r.data); setD(r.data); setChanged(false);
      toast.success(t("common.saved"));
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const startPublish = async (md) => {
    if (changed && !(await save())) return;
    setMode(md);
  };
  const props = { q, d, set, m, locked, reload: load, showCost: can("cost:view") };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/quotations" className="rounded-full p-1.5 hover:bg-white" data-testid="back-to-quotes"><ArrowLeft className="h-4 w-4" /></Link>
        <Folio>{q.folio}</Folio>
        <StatusBadge status={q.status} testid="quote-status-badge" />
        <span data-testid="quote-rev-badge" className="rounded-sm bg-black px-2 py-0.5 font-mono text-[11px] font-bold text-white">{q.current_rev == null ? t("q.norev") : `Rev ${q.current_rev}`}</span>
        {(changed || (q.dirty && q.current_rev != null)) && <span data-testid="unpublished-indicator" className="text-[11px] font-semibold text-amber-700">● {t("q.unpublished")}</span>}
        {locked && <span className="inline-flex items-center gap-1 text-xs text-neutral-500"><Lock className="h-3 w-3" />{closed ? t("ws.locked") : t("ws.readonly")}</span>}
        <div className="min-w-0 basis-full truncate text-sm font-semibold text-neutral-700 sm:basis-auto sm:flex-1">{q.resolved?.customer_name} {q.title && `· ${q.title}`}</div>
        {q.current_rev != null && (
          <Button variant="outline" className="bg-white" data-testid="download-pdf-btn"
            onClick={() => downloadPdf(`/quotations/${id}/revisions/${q.current_rev}/pdf`, `${q.folio}-Rev${q.current_rev}.pdf`).catch((e) => toast.error(errMsg(e)))}>
            <Download className="h-4 w-4" />{t("ws.downloadPdf")} (Rev {q.current_rev})
          </Button>
        )}
        {!locked && (
          <div className="flex flex-wrap gap-2">
            <Button variant={isNew ? "default" : "outline"} data-testid="save-draft-btn" onClick={save} className={isNew ? "" : "bg-white"}><Save className="h-4 w-4" />{isNew ? t("q.create") : t("ws.saveDraft")}</Button>
            {can("quote:publish") && !isNew && <>
              <Button variant="outline" data-testid="create-revision-btn" onClick={() => startPublish("rev")} className="bg-white"><GitBranch className="h-4 w-4" />{t("ws.createRev")}</Button>
              <Button data-testid="publish-send-btn" onClick={() => startPublish("send")}><Send className="h-4 w-4" />{t("ws.publish")}</Button>
            </>}
          </div>
        )}
      </div>
      {isNew && <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="new-quote-hint">{t("q.newHint")}</div>}
      <Summary calc={q.calc} currency={d.currency} showCost={props.showCost} />
      <Warnings calc={q.calc} />
      <SectionNav keys={isNew ? ["commercial", "costs", "fiscal", "tech"] : ["commercial", "costs", "fiscal", "tech", "revisions", "pdf", "acceptance"]}>
        {{
          commercial: <CommercialTab {...props} />, costs: <LinesTab {...props} />, fiscal: <FiscalTab {...props} />, tech: <TechTab {...props} />,
          revisions: <RevisionsTab {...props} key={revKey} />, pdf: <PdfTab {...props} key={revKey} />, acceptance: <AcceptanceTab {...props} key={`a${revKey}`} />,
        }}
      </SectionNav>
      <PublishDialog mode={mode} qid={id} onClose={() => setMode(null)} onDone={() => { setMode(null); load(); setRevKey((k) => k + 1); }} />
    </div>
  );
}
